/**
 * Project visit id (RFC-1577): one random id per browser tab × project
 * workspace entry. A new id is minted each time the workspace for a project is
 * entered in this tab; it stays stable while the user remains in it, and ends
 * when they leave (route change, unmount, pagehide).
 *
 * Module-scoped and in-memory only (never persisted): a reload or a new tab is a
 * new visit. Analytics code inside the workspace (generation submits,
 * downloads) reads the same id through `getProjectVisitId` /
 * `peekProjectVisitId`. The lifecycle is driven by one `useProjectVisit` call
 * in WorkspacePageV2; it emits no event of its own.
 *
 * Ordering: React runs child effects before parent effects, so a child surface
 * can ask for the id before the workspace's hook has recorded the entry.
 * `getProjectVisitId` therefore mints lazily, and `enterProjectVisit` adopts an
 * id minted that way for the same project instead of minting a second one.
 */

import { useEffect } from 'react'

interface Visit {
  projectId: string
  id: string
  /** True once `enterProjectVisit` has claimed this visit. */
  entered: boolean
}

let current: Visit | null = null

function newVisitId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/**
 * The current visit id for `projectId`, minting one if this tab has no open
 * visit for that project. Never throws.
 */
export function getProjectVisitId(projectId: string): string {
  if (current && current.projectId === projectId) return current.id
  current = { projectId, id: newVisitId(), entered: false }
  return current.id
}

/**
 * The open visit id for `projectId`, or null when there is none. For callers
 * that must not start a visit themselves (e.g. code that may run outside the
 * workspace).
 */
export function peekProjectVisitId(projectId: string): string | null {
  return current && current.projectId === projectId ? current.id : null
}

/**
 * Record a workspace entry for `projectId` and return its visit id. Adopts an
 * id lazily minted for the same project since the last exit; otherwise (a
 * second entry, or another project) mints a fresh one.
 */
export function enterProjectVisit(projectId: string): string {
  if (current && current.projectId === projectId && !current.entered) {
    current.entered = true
    return current.id
  }
  current = { projectId, id: newVisitId(), entered: true }
  return current.id
}

/** End the visit for `projectId` (no-op if another project's visit is open). */
export function exitProjectVisit(projectId: string): void {
  if (current && current.projectId === projectId) current = null
}

// ── Workspace lifecycle ─────────────────────────────────────────────────

/**
 * Unmount → remount of the workspace for the SAME project inside one task
 * (React StrictMode's dev double-mount, a route/provider remount on a canvas
 * switch or view toggle) is not a new entry: the unmount only schedules the
 * exit, and a remount for that project before it runs cancels it and keeps the
 * visit. A different project ends the pending visit first.
 */
export const VISIT_EXIT_GRACE_MS = 0

interface VisitSession {
  projectId: string
  /** Pending deferred exit (set while unmounted within the grace window). */
  exitTimer: ReturnType<typeof setTimeout> | null
}

let session: VisitSession | null = null
let mountedProject: string | null = null
let mountCount = 0
let pageListeners = false

function hasWindow(): boolean {
  return typeof window !== 'undefined' && typeof window.addEventListener === 'function'
}

function endSession(s: VisitSession): void {
  if (s.exitTimer !== null) {
    clearTimeout(s.exitTimer)
    s.exitTimer = null
  }
  exitProjectVisit(s.projectId)
  if (session === s) session = null
}

function startSession(projectId: string): void {
  enterProjectVisit(projectId)
  session = { projectId, exitTimer: null }
}

function onPageHide(): void {
  if (session) endSession(session)
}

// A page restored from the back/forward cache never remounts, so treat the
// restore as a new workspace entry (the pagehide before it ended the visit).
function onPageShow(event: PageTransitionEvent): void {
  if (event.persisted && !session && mountedProject !== null && mountCount > 0) {
    startSession(mountedProject)
  }
}

function ensurePageListeners(): void {
  if (pageListeners || !hasWindow()) return
  window.addEventListener('pagehide', onPageHide)
  window.addEventListener('pageshow', onPageShow)
  pageListeners = true
}

/**
 * Start (or resume) the visit for `projectId` and return the unmount function,
 * which ends it after the grace window. Exported for tests; components use the
 * hook. Never throws.
 */
export function startProjectVisit(projectId: string): () => void {
  try {
    mountCount++
    mountedProject = projectId
    ensurePageListeners()
    const existing = session
    if (existing && existing.projectId === projectId && existing.exitTimer !== null) {
      // Remount within the grace window: same visit.
      clearTimeout(existing.exitTimer)
      existing.exitTimer = null
    } else {
      if (existing) endSession(existing)
      startSession(projectId)
    }
  } catch {
    /* telemetry never breaks the product */
  }

  let stopped = false
  return () => {
    if (stopped) return
    stopped = true
    mountCount = Math.max(0, mountCount - 1)
    const s = session
    if (!s || s.projectId !== projectId || s.exitTimer !== null) return
    s.exitTimer = setTimeout(() => {
      s.exitTimer = null
      if (session === s) endSession(s)
    }, VISIT_EXIT_GRACE_MS)
  }
}

/** Keep a project visit open while the workspace for `projectId` is mounted. */
export function useProjectVisit(projectId: string | null | undefined): void {
  useEffect(() => {
    if (!projectId) return
    return startProjectVisit(projectId)
  }, [projectId])
}

/** Test helper — forget the open visit, the lifecycle state and listeners. */
export function _resetProjectVisitForTest(): void {
  if (session?.exitTimer) clearTimeout(session.exitTimer)
  session = null
  current = null
  mountedProject = null
  mountCount = 0
  if (pageListeners && hasWindow()) {
    window.removeEventListener('pagehide', onPageHide)
    window.removeEventListener('pageshow', onPageShow)
  }
  pageListeners = false
}
